import {
  DeltaAction,
  FlagSeverity,
  IntakeChannel,
  Movement,
  type ReviewItemResponse,
  type ValidationFlagResponse,
} from '../../api';

/**
 * The words the back-office uses, and the two rules about how it reads.
 *
 * Everything here is a lookup from a value the contract defines to the pt-BR an
 * analyst reads. It is a module rather than inline JSX for the reason every
 * other label table in this application is: the same movement appears in three
 * listings, and "Exclusão" spelled two ways in two of them is how a screen stops
 * being trusted.
 *
 * The flag messages are the exception, and deliberately so. They come from the
 * server, in the server's own words, because they are written for whoever has to
 * act on them and the server is the only thing that knows *why* a particular
 * movement was flagged -- "nobody with that CPF is insured on 2026-05-31" is a
 * sentence a fallback table here could not produce. What this module contributes
 * is the *heading* for each kind, so an analyst can see at a glance which sort
 * of problem they are looking at.
 */

export const ACTION_LABELS: Record<string, string> = {
  [DeltaAction.ADMISSION]: 'Inclusão',
  [DeltaAction.TERMINATION]: 'Exclusão',
  [DeltaAction.SALARY_CHANGE]: 'Alteração salarial',
  [DeltaAction.LEAVE]: 'Afastamento',
};

/**
 * What a line of the file did since the previous cycle, in the words the
 * movements already use. An unchanged line is a dash, as is a line stored
 * before movements were recorded: neither moved anybody.
 */
export const MOVEMENT_LABELS: Record<string, string> = {
  [Movement.INCLUSION]: 'Inclusão',
  [Movement.ALTERATION]: 'Alteração',
  [Movement.EXCLUSION]: 'Exclusão',
  [Movement.UNCHANGED]: '—',
};

/**
 * Rows per page in both views. #425's pager has no size of its own; this is the
 * one the Segurados lists page by, so every client-side list in the back-office
 * turns its pages at the same place.
 */
export const PREVIEW_PAGE_SIZE = 50;

export const CHANNEL_LABELS: Record<string, string> = {
  [IntakeChannel.SPREADSHEET]: 'Planilha',
  // Not "Governo": what arrived is the *employer's* own filing, which the
  // government holds a copy of. An analyst who read "Governo" would think the
  // Receita had asked for something.
  [IntakeChannel.ESOCIAL]: 'eSocial',
  [IntakeChannel.PORTAL]: 'Portal',
  [IntakeChannel.API]: 'API',
  [IntakeChannel.BACK_OFFICE]: 'Back-office',
};

/** What each kind of problem is called. The sentence beside it is the server's. */
export const FLAG_LABELS: Record<string, string> = {
  roster_conflict: 'Conflito com o cadastro',
  late_arrival: 'Fora do período já enviado',
  missing_evidence: 'Sem documento comprobatório',
  duplicate_in_cycle: 'Mais de uma movimentação para a pessoa',
};

export const SEVERITY_LABELS: Record<string, string> = {
  [FlagSeverity.BLOCKING]: 'Impeditivo',
  [FlagSeverity.WARNING]: 'Atenção',
};

export const STATUS_LABELS: Record<string, string> = {
  submitted: 'Aguardando análise',
  approved: 'Aprovada',
  rejected: 'Recusada',
  returned: 'Devolvida',
  cancelled: 'Cancelada',
};

export const REVIEW_COPY = {
  title: 'Fila de análise',
  description:
    'As movimentações que entram no fechamento deste ciclo, por contrato. Analise as ' +
    'sinalizadas e aprove o ciclo quando estiver conforme.',
  cycleLabel: 'Ciclo',
  contractsTitle: 'Contratos',
  contractsDescription: 'Um contrato por linha, com o que está esperando análise em cada um.',
  currentTitle: 'Movimentações deste ciclo',
  upcomingTitle: 'Agendadas para ciclos futuros',
  upcomingDescription:
    'Já enviadas pelo cliente, com vigência depois deste corte. Cada uma mostra em qual ' +
    'ciclo vai entrar.',
  lateTitle: 'Chegaram depois do envio',
  lateDescription:
    'A vigência cai dentro de um mês cujo arquivo já foi enviado à operadora. Não entram ' +
    'neste ciclo e não há caminho de correção por aqui — depende de definirmos o que a ' +
    'operadora aceita.',
  previewTitle: 'Prévia do arquivo',
  previewDescription: 'O que este fechamento produziria, comparado com o último arquivo aprovado.',
  buildPreview: 'Gerar prévia',
  rebuildPreview: 'Gerar prévia de novo',
  approveCycle: 'Aprovar ciclo',
  reopenCycle: 'Reabrir ciclo',
  approved: 'Ciclo aprovado.',
  reopened: 'Ciclo reaberto.',
  blockedFromApproval:
    'Há movimentações com problema impeditivo neste ciclo. Decida cada uma antes de aprovar.',
  needsPreview: 'Gere a prévia antes de aprovar: é ela que fica registrada na aprovação.',
  previewViewsLabel: 'O que mostrar da prévia',
  differencesView: 'Diferenças',
  fileView: 'Arquivo',
  differenceKindLabel: 'Tipo de diferença',
  allDifferences: 'Todas',
  addedDifferences: 'Incluídas',
  removedDifferences: 'Removidas',
  changedDifferences: 'Alteradas',
  differencesCaption: 'Diferenças em relação ao último arquivo aprovado',
  noDifferences: 'Nada mudou desde o último arquivo aprovado',
  noDifferencesOfKind: 'Nenhuma diferença deste tipo',
  fileCaption: 'Linhas do arquivo que este fechamento produziria',
  fileEmpty: 'O arquivo não tem linhas',
  fileLoading: 'Carregando o arquivo…',
  fileError: 'Não foi possível carregar o arquivo',
  movementHeader: 'Movimentação',
  exceptionsTitle: 'Não renderizadas',
  exceptionsCaption: 'Pessoas que o layout não conseguiu escrever no arquivo',
  previewBuilt: 'Prévia gerada.',
  // One toast per decision, not one for the decision and another for the
  // preview it rebuilt.
  decidedPreviewUpdated: 'Decisão registrada. A prévia foi atualizada.',
  previewUpdating: 'Atualizando a prévia com a última decisão…',
  previewStale:
    'A prévia não reflete a última decisão. Gere a prévia de novo antes de aprovar o ciclo.',
  noItems: 'Nada esperando análise neste ciclo.',
  noUpcoming: 'Nada agendado para ciclos futuros.',
  noLate: 'Nenhuma movimentação chegou depois do envio.',
  noContracts: 'Nenhum contrato no seu escopo.',
  documentTitle: 'Documento comprobatório',
  documentEmpty: 'Nenhum documento vinculado a esta movimentação.',
  closeDocument: 'Fechar documento',
  approveItem: 'Aprovar',
  rejectItem: 'Recusar',
  returnItem: 'Devolver',
  correctDate: 'Corrigir vigência',
  // The button that opens the form and the button that submits it say different
  // things on purpose. "Devolver" twice on one screen is two buttons a person
  // has to tell apart by position, which is exactly what a confirmation step is
  // meant to stop being possible.
  confirmReject: 'Confirmar recusa',
  confirmReturn: 'Confirmar devolução',
  confirmCorrection: 'Confirmar nova vigência',
  reasonLabel: 'Motivo',
  reasonHint: 'O cliente lê este texto, então diga o que precisa ser corrigido.',
  correctedFrom: 'Enviada como',
  // Said as a fact about the *check*, not about the person: the movement was
  // accepted, and what is missing is the confirmation against the Receita. A
  // movement that actually contradicted the Receita never reaches this screen --
  // it is refused when the customer files it.
  identityUnchecked: 'Sem conferência na Receita',
  identityUncheckedHint:
    'Não foi possível conferir CPF, nome e data de nascimento na Receita Federal quando ' +
    'esta inclusão foi enviada. Confira a documentação da pessoa antes de aprovar.',
} as const;

/**
 * Whether this movement is an inclusão whose person nobody confirmed.
 *
 * `identity_verified` is a tri-state and only one of its three values is worth a
 * marker on the screen. `true` is the ordinary case and needs no decoration;
 * `null` means the question does not arise -- the movement is not an inclusão,
 * or it predates the check -- and a badge on those would put "não conferida" on
 * every termination in the queue. `false` is the one that matters: the check
 * never ran, and the analyst is about to approve an inclusão on the customer's
 * word alone.
 *
 * A named predicate rather than an inline comparison because the generated
 * field is optional *and* nullable, and `=== false` written in two places is two
 * chances to write `!` and turn every movement into a warning.
 */
export function identityWasNotChecked(item: ReviewItemResponse): boolean {
  return item.delta.identity_verified === false;
}

/** The most serious flag on an item, or `null` when it carries none. */
export function worstFlag(item: ReviewItemResponse): ValidationFlagResponse | null {
  const blocking = item.flags.find((flag) => flag.severity === FlagSeverity.BLOCKING);
  return blocking ?? item.flags[0] ?? null;
}

/**
 * Whether this item's date has been corrected by an analyst.
 *
 * A named predicate because the generated field is optional *and* nullable, and
 * `item.delta.original_effective_date != null` written inline in three places is
 * three chances to write `!==` and get the undefined case wrong.
 */
export function wasDateCorrected(item: ReviewItemResponse): boolean {
  return (
    item.delta.original_effective_date !== null && item.delta.original_effective_date !== undefined
  );
}

/** The current month as the API writes a cycle: `YYYY-MM`. */
export function currentCycleMonth(today: Date = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
}
